import React, { useState, useEffect, useRef } from 'react';
import './aiSuggestionModal.scss';
import {
  GeckoIcon,
  GeckoLabel,
  GeckoBtnsSorted,
  GeckoToastService,
  Tooltip,
  OverlayTrigger,
} from 'ui';
import Typing from '../../../conversations/components/Typing';
import { stateFromHTML } from 'draft-js-import-html';
import { convertToRaw } from 'draft-js';
import parse from 'html-react-parser';

const AiSuggestionModal = ({
  onInsert = () => {},
  handleAiSuggestion = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const aiSuggestionContainer = useRef(null);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const noReplyMessage = 'No AI Suggestion Found';

  useEffect(() => {
    const mouseDownHandler = (event) => {
      // If the selector isn't open or we don't have a handle on it's container return early
      if (!open || aiSuggestionContainer.current === null) {
        return;
      }
      // If the click was inside the container return early
      if (aiSuggestionContainer.current.contains(event.target)) {
        return;
      }
      // Otherwise close the modal
      closeModal();
    };
    window.addEventListener('mousedown', mouseDownHandler);
    return () => {
      window.removeEventListener('mousedown', mouseDownHandler);
    };
  }, [open]);

  const fetchAiSuggestion = async () => {
    try {
      const suggestion = await handleAiSuggestion();
      if (!suggestion) {
        setAiSuggestion(noReplyMessage);
      } else {
        setAiSuggestion(suggestion);
      }
      setIsLoading(false);
    } catch (err) {
      new GeckoToastService().error('Failed to fetch AI Samples');
    }
  };

  const closeModal = () => {
    setOpen(false);
    setIsLoading(true);
    setAiSuggestion('');
  };

  const openModal = () => {
    if (open) {
      return closeModal();
    }
    setOpen(true);
    setIsLoading(true);
    fetchAiSuggestion();
  };

  const insert = (suggestion) => {
    let options = {
      customBlockFn: (element) => {
        if (element.tagName === 'P') {
          return { type: 'HTML_PARAGRAPHS' };
        }
      },
    };
    const contentState = stateFromHTML(suggestion, options);
    const editorObject = convertToRaw(contentState);
    const blockWithSpacing = editorObject.blocks.map((b) => {
      return b.type === 'HTML_PARAGRAPHS'
        ? {
            ...b,
            text: `${b.text}\n`,
            type: 'unstyled',
          }
        : b;
    });

    const newEditorObj = {
      ...editorObject,
      blocks: blockWithSpacing,
    };

    const formattedSuggestion = { editor_object: newEditorObj };
    onInsert(formattedSuggestion);
    closeModal();
  };
  const betaTooltipText =
    'ChatGPT generated responses are still in Beta, so please check the response carefully before sending';
  const buttons = [
    {
      name: 'Dismiss',
      type: 'default',
      handler: () => closeModal(),
    },
    {
      name: 'Use Generated Reply',
      type: 'primary',
      handler: () => {
        insert(aiSuggestion);
      },
      disabledWhen: () =>
        (!aiSuggestion || aiSuggestion === noReplyMessage) && !isLoading,
    },
  ];

  const renderModal = () => {
    if (!open) {
      return null;
    }

    return (
      <div
        className="ai-suggestion-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`xx-header`}
      >
        <div className="ai-suggestion-header">
          <h4 id={`xx-header`} className="ai-suggestion-title">
            <GeckoIcon icon="far fa-wand-magic-sparkles gradient-icon" />
            <span className="title-text">Suggested Reply</span>{' '}
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              overlay={<Tooltip id="beta">{betaTooltipText}</Tooltip>}
            >
              <span className="beta-label" id="beta">
                <GeckoLabel name="Beta" type="primary" />
              </span>
            </OverlayTrigger>
          </h4>
          <span
            className="ai-suggestion-close"
            aria-label="Close"
            onClick={() => closeModal()}
          >
            <GeckoIcon icon="far fa-times" />
          </span>
        </div>

        <div className="ai-suggestion-body">
          {isLoading ? (
            <div style={{ paddingLeft: '10px' }}>
              <Typing />
            </div>
          ) : (
            aiSuggestion && parse(aiSuggestion)
          )}
        </div>

        <div className="ai-suggestion-footer">
          <GeckoBtnsSorted items={buttons} />
        </div>
      </div>
    );
  };

  return (
    <div
      ref={aiSuggestionContainer}
      className={`ai-suggestion-menu-item ${open ? 'open' : null}`}
    >
      {renderModal()}
      <span onClick={openModal}>
        <OverlayTrigger
          placement="top"
          delay={{ show: 0, hide: 0 }}
          overlay={<Tooltip id="ai-icon">Generate AI Reply</Tooltip>}
        >
          <i
            className="far fa-wand-magic-sparkles gradient-icon"
            id="ai-icon"
          ></i>
        </OverlayTrigger>
      </span>
    </div>
  );
};

export default AiSuggestionModal;
